import React, { useEffect, useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SparklesIcon } from 'lucide-react';
export function BottleFeaturesSection() {
  const [showNewBottle, setShowNewBottle] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Scroll reveal hooks
  const headerReveal = useScrollReveal({
    threshold: 0.2
  });
  const bottleReveal = useScrollReveal({
    threshold: 0.2
  });
  const leftFeaturesReveal = useScrollReveal({
    threshold: 0.1
  });
  const rightFeaturesReveal = useScrollReveal({
    threshold: 0.1
  });
  // Magic Transformation Logic
  useEffect(() => {
    const interval = setInterval(() => {
      // 1. Start the transition animation (blur/scale)
      setIsTransitioning(true);
      // 2. Wait for the "Flash" point to swap the images
      setTimeout(() => {
        setShowNewBottle(prev => !prev);
      }, 400); // Swap happening exactly at the peak of the flash
      // 3. End transition state to settle back to normal
      setTimeout(() => {
        setIsTransitioning(false);
      }, 1000);
    }, 4000); // Switch every 4 seconds
    return () => clearInterval(interval);
  }, []);
  const features = [{
    title: 'Joint Comfort',
    subtitle: 'Support your daily mobility'
  }, {
    title: '65% Boswellic Acids',
    subtitle: 'One of the highest concentrations available'
  }, {
    title: 'Clinically-Studied',
    subtitle: 'Backed by research'
  }, {
    title: 'Pure Formula',
    subtitle: 'No fillers. No binders. No stearate.'
  }, {
    title: 'Vegan & Non-GMO',
    subtitle: 'Clean ingredients only'
  }, {
    title: '30 Capsules',
    subtitle: 'Daily comfort for your temple'
  }];
  return <section className="w-full relative py-16 lg:py-20 overflow-hidden bg-[#f9f9f7]" data-id="element-7">
      {/* Background Image */}
      <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{
      backgroundImage: 'url(https://uploadthingy.s3.us-west-1.amazonaws.com/bvviSaRhveVQFKCssTD388/image.png)'
    }} data-id="element-8" />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white/80" data-id="element-9"></div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12" data-id="element-10">
        {/* Header Section */}
        <div ref={headerReveal.ref} className={`text-center mb-12 lg:mb-16 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-11">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-3 tracking-tight text-[#496f58]" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-12">
            Renewed Design.{' '}
            <span className="relative inline-block" data-id="element-13">
              <span className="absolute bottom-1 left-0 w-full h-[55%] bg-[#ffe175] opacity-90 transform -skew-x-6 -rotate-1 rounded-sm -z-10" data-id="element-14"></span>
              <span className="italic relative z-10" data-id="element-15">Faithful</span>
            </span>{' '}
            Formula.
          </h2>
          <p className="text-gray-700 text-lg md:text-xl max-w-3xl mx-auto font-light" data-id="element-16">
            The same powerful capsule you trust, now in a beautiful new vessel
          </p>
        </div>

        {/* --- MOBILE LAYOUT --- */}
        <div className="lg:hidden" data-id="element-17">
          {/* Mobile Product Image Container */}
          <div className="flex justify-center items-center mb-8 h-[400px] relative" data-id="element-18">
            {/* Background Glow (Added to Mobile) */}
            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-[#496f58]/20 blur-[50px] transition-all duration-500 ${isTransitioning ? 'scale-125 bg-[#d4af37]/30' : 'scale-100'}`} data-id="element-19" />

            {/* The Magic Container */}
            <div className="relative w-[280px] h-[380px] flex items-center justify-center" data-id="element-20">
              {/* 1. Shockwave Effect (Added to Mobile) */}
              <div className={`absolute inset-0 border-2 border-[#d4af37]/50 rounded-full z-0 ${isTransitioning ? 'animate-shockwave opacity-100' : 'opacity-0'}`} data-id="element-21"></div>

              {/* 2. Particles (Added to Mobile) */}
              <div className="absolute inset-0 z-10 pointer-events-none" data-id="element-22">
                <div className="particle p1" data-id="element-23"></div>
                <div className="particle p2" data-id="element-24"></div>
                <div className="particle p3" data-id="element-25"></div>
              </div>
              {/* 4. Images Wrapper */}
              <div className={`relative w-full h-full transition-all duration-250 ease-out transform ${isTransitioning ? 'scale-95 blur-xs brightness-110' : 'scale-100 blur-0 brightness-100'}`} data-id="element-26">
                {/* Old Bottle */}
                <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/Untitled_design_37_00f913a3-5f85-4d3a-9578-9d483b992b05.png?v=1764370989" alt="Original Bottle" className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl" style={{
                opacity: showNewBottle ? 0 : 1,
                transition: 'none',
                transform: 'scale(2)'
              }} data-id="element-27" />

                {/* New Bottle */}
                <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/843ed722-fcab-49c4-9afc-c384ca51f27b_1.png?v=1764371019" alt="New Bottle" className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl" style={{
                opacity: showNewBottle ? 1 : 0,
                transition: 'none',
                transform: 'scale(1.5)'
              }} data-id="element-28" />
              </div>
            </div>
          </div>

          {/* Text Under Bottle */}
          <div className="text-center mb-8 px-4" data-id="element-29">
            <div className="max-w-md mx-auto space-y-2" data-id="element-30">
              <p className="text-[#496f58] font-bold text-lg" style={{
              fontFamily: "'Playfair Display', serif",
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }} data-id="element-31">
                New Design Launching Soon
              </p>
              <p className="text-gray-700 text-sm font-medium" style={{
              fontFamily: "'Inter', sans-serif"
            }} data-id="element-32">
                Save now on our original bottle • Limited stock
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3" data-id="element-33">
            <button className="bg-[#496f58] text-white font-bold px-8 py-3 rounded-full shadow-lg uppercase text-sm tracking-wide w-full" data-id="element-34">
              Nutritional Information
            </button>
            <button className="bg-[#2d4a3a] text-white font-bold px-8 py-3 rounded-full shadow-lg uppercase text-sm tracking-wide w-full" data-id="element-35">
              Ingredients
            </button>
          </div>
        </div>

        {/* --- DESKTOP LAYOUT --- */}
        <div className="hidden lg:block" data-id="element-36">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-12 items-center" data-id="element-37">
            {/* Left Features */}
            <div ref={leftFeaturesReveal.ref} className={`space-y-6 text-right transition-all duration-1000 ${leftFeaturesReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`} data-id="element-38">
              {features.slice(0, 3).map((feature, index) => <div key={index} className="bg-white/80 backdrop-blur-md rounded-xl p-5 shadow-lg border border-white hover:border-[#496f58]/30 transition-all group" style={{
              transitionDelay: `${index * 150}ms`
            }} data-id="element-39">
                  <h3 className="font-bold text-[#2d4a3a] text-lg mb-1 group-hover:text-[#496f58] transition-colors" data-id="element-40">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-41">{feature.subtitle}</p>
                </div>)}
            </div>

            {/* Center: The Magic Show */}
            <div ref={bottleReveal.ref} className="flex flex-col justify-center items-center relative" data-id="element-42">
              {/* Glow Behind */}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full bg-[#496f58]/20 blur-[80px] transition-all duration-500 ${isTransitioning ? 'scale-125 bg-[#d4af37]/30' : 'scale-100'}`} data-id="element-43" />

              {/* The Bottle Container */}
              <div className="relative w-[500px] h-[600px] flex items-center justify-center overflow-visible" data-id="element-44">
                {/* 1. Shockwave */}
                <div className={`absolute inset-0 border-2 border-[#d4af37]/50 rounded-full z-0 ${isTransitioning ? 'animate-shockwave opacity-100' : 'opacity-0'}`} data-id="element-45"></div>

                {/* 2. Particles */}
                <div className="absolute inset-0 z-10 pointer-events-none" data-id="element-46">
                  <div className="particle p1" data-id="element-47"></div>
                  <div className="particle p2" data-id="element-48"></div>
                  <div className="particle p3" data-id="element-49"></div>
                </div>
                {/* 4. Images Wrapper */}
                <div className={`relative w-full h-full transition-all duration-500 ease-out transform ${isTransitioning ? 'scale-95 blur-xs brightness-110' : 'scale-100 blur-0 brightness-100'}`} data-id="element-50">
                  {/* Old Bottle */}
                  <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/Untitled_design_37_00f913a3-5f85-4d3a-9578-9d483b992b05.png?v=1764370989" alt="Original Bottle" className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl" style={{
                  opacity: showNewBottle ? 0 : 1,
                  transition: 'none',
                  transform: 'scale(2)'
                }} data-id="element-51" />

                  {/* New Bottle */}
                  <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/843ed722-fcab-49c4-9afc-c384ca51f27b_1.png?v=1764371019" alt="Renewed Bottle" className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl" style={{
                  opacity: showNewBottle ? 1 : 0,
                  transition: 'none',
                  transform: 'scale(1.5)'
                }} data-id="element-52" />
                </div>
              </div>

              {/* Text Under Bottle */}
              <div className="text-center mb-8 px-4" data-id="element-53">
                <div className="max-w-md mx-auto space-y-2" data-id="element-54">
                  <p className="text-[#496f58] font-bold text-lg" style={{
                  fontFamily: "'Playfair Display', serif",
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} data-id="element-55">
                    New Design Launching Soon
                  </p>
                  <p className="text-gray-700 text-sm font-medium" style={{
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-56">
                    Save now on our original bottle • Limited stock
                  </p>
                </div>
              </div>
            </div>

            {/* Right Features */}
            <div ref={rightFeaturesReveal.ref} className={`space-y-6 text-left transition-all duration-1000 ${rightFeaturesReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} data-id="element-57">
              {features.slice(3, 6).map((feature, index) => <div key={index} className="bg-white/80 backdrop-blur-md rounded-xl p-5 shadow-lg border border-white hover:border-[#496f58]/30 transition-all group" style={{
              transitionDelay: `${index * 150}ms`
            }} data-id="element-58">
                  <h3 className="font-bold text-[#2d4a3a] text-lg mb-1 group-hover:text-[#496f58] transition-colors" data-id="element-59">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-60">{feature.subtitle}</p>
                </div>)}
            </div>
          </div>

          {/* Desktop CTAs */}
          <div className="flex flex-wrap justify-center gap-4 mt-16" data-id="element-61">
            <button className="bg-[#496f58] hover:bg-[#3d5a47] hover:-translate-y-1 text-white font-bold px-10 py-4 rounded-full transition-all shadow-xl uppercase text-sm tracking-widest" data-id="element-62">
              Nutritional Information
            </button>
            <button className="bg-[#2d4a3a] hover:bg-[#1f3329] hover:-translate-y-1 text-white font-bold px-10 py-4 rounded-full transition-all shadow-xl uppercase text-sm tracking-widest" data-id="element-63">
              Ingredients
            </button>
          </div>
        </div>
      </div>

      <style data-id="element-64">{`
        @keyframes shockwave {
          0% { transform: scale(0.8); opacity: 1; border-width: 4px; }
          100% { transform: scale(1.5); opacity: 0; border-width: 0px; }
        }
        
        .animate-shockwave {
          animation: shockwave 0.8s ease-out forwards;
        }

        /* Floating Particles */
        .particle {
          position: absolute;
          background: #d4af37; /* Gold */
          border-radius: 50%;
          opacity: 0;
        }

        .p1 { width: 6px; height: 6px; top: 20%; left: 20%; animation: floatP1 4s infinite; }
        .p2 { width: 8px; height: 8px; top: 60%; right: 20%; animation: floatP2 5s infinite; }
        .p3 { width: 4px; height: 4px; top: 30%; right: 30%; animation: floatP3 3.5s infinite; }

        @keyframes floatP1 {
          0%, 100% { transform: translate(0, 0); opacity: 0; }
          50% { transform: translate(10px, -20px); opacity: 0.8; }
        }
        @keyframes floatP2 {
            0%, 100% { transform: translate(0, 0); opacity: 0; }
            50% { transform: translate(-15px, -25px); opacity: 0.6; }
        }
        @keyframes floatP3 {
            0%, 100% { transform: translate(0, 0); opacity: 0; }
            50% { transform: translate(5px, -15px); opacity: 0.9; }
        }
      `}</style>
    </section>;
}